// SKFitter plots, drawn like the desktop's (skfitter_jobs.draw_dft_bands / draw_fit_window / draw_fit / draw_convergence):
// band structures on the k-path with the high-symmetry points marked and E_F dashed.
import type { BandView, FitResult, SegTrace, SetupView } from "../lib/skfitter";

const INK = "#0b0b0b", AXIS = "#c3c2b7", GRID = "#e1e0d9", ORANGE = "#eb6834", BLUE = "#2a78d6";

function kpathAxis(ticks: number[], labels: string[] | null, range: [number, number], anchor?: string) {
  return {
    range, tickvals: ticks, ticktext: labels ?? ticks.map(() => ""), showgrid: false, zeroline: false,
    showline: true, linecolor: AXIS, ...(anchor ? { anchor } : {}),
  };
}

function nodeLines(ticks: number[], xref: string, yref: string) {
  return ticks.map((t) => ({ type: "line", xref, yref: `${yref} domain`, x0: t, x1: t, y0: 0, y1: 1, layer: "below",
    line: { color: GRID, width: 1 } }));
}

function efLine(xref: string, yref: string) {
  return { type: "line", xref: `${xref} domain`, yref, x0: 0, x1: 1, y0: 0, y1: 0, layer: "below",
    line: { color: AXIS, width: 1, dash: "dash" } };
}

function segTraces(traces: SegTrace[], xaxis = "x", yaxis = "y", name?: string) {
  return traces.map((t) => ({ x: t.x, y: t.y, type: "scattergl", mode: "lines", xaxis, yaxis, hoverinfo: "skip",
    line: { color: t.color, width: t.width }, showlegend: false, name }));
}

const legendTrace = (label: string, color: string, dash?: string) =>
  ({ x: [null], y: [null], type: "scatter", mode: "lines", name: label, line: { color, width: 3, ...(dash ? { dash } : {}) } });

/** The DFT band plot: one panel per spin (side by side), coloured segments, legend or colour bar. */
export function bandFigure(v: BandView) {
  const n = v.panels.length;
  const gap = 0.04;
  const w = (1 - gap * (n - 1)) / n;
  const data: any[] = [];
  const layout: any = { showlegend: v.legend.length > 0, legend: { orientation: "h", x: 1, xanchor: "right", y: 1.08 },
    shapes: [], annotations: [], margin: { l: 60, r: v.colorbar ? 90 : 20, t: 50, b: 40 } };
  v.panels.forEach((p, i) => {
    const xa = i === 0 ? "x" : `x${i + 1}`, ya = i === 0 ? "y" : `y${i + 1}`;
    data.push(...segTraces(p.traces, xa, ya));
    layout[i === 0 ? "xaxis" : `xaxis${i + 1}`] = { ...kpathAxis(v.ticks, v.tick_labels, v.x_range, ya), domain: [i * (w + gap), i * (w + gap) + w] };
    layout[i === 0 ? "yaxis" : `yaxis${i + 1}`] = { range: v.window, anchor: xa, zeroline: false, gridcolor: GRID,
      ...(i === 0 ? { title: { text: "E − E<sub>F</sub> (eV)" } } : { matches: "y", showticklabels: false }) };
    layout.shapes.push(...nodeLines(v.ticks, xa, ya), efLine(xa, ya));
    layout.annotations.push({ xref: `${xa} domain`, yref: `${ya} domain`, x: 0, y: 1.02, xanchor: "left", yanchor: "bottom",
      text: p.title, showarrow: false, font: { size: 13 } });
  });
  v.legend.forEach((l) => data.push(legendTrace(l.label, l.color)));
  if (v.colorbar) {
    const cs = v.colorbar.colors.map((c, i, a) => [i / (a.length - 1), c]);
    data.push({ x: [null], y: [null], type: "scatter", mode: "markers", showlegend: false, hoverinfo: "skip",
      marker: { color: [0], cmin: 0, cmax: v.colorbar.max, colorscale: cs, showscale: true,
        colorbar: { title: { text: v.colorbar.label, side: "right" }, thickness: 12 } } });
  }
  if (v.note) layout.annotations.push({ xref: "paper", yref: "paper", x: 0.01, y: 0.02, xanchor: "left", text: v.note,
    showarrow: false, font: { size: 11, color: "#898781" } });
  return { data, layout };
}

/** "Bands used in the fit": every DFT band grey, the fitted ones orange. */
export function windowFigure(p: NonNullable<SetupView["plot"]>, count: number) {
  const data = [...segTraces(p.others), ...segTraces(p.fitted), legendTrace(`fitted (${count} bands)`, ORANGE), legendTrace("not fitted", AXIS)];
  return {
    data,
    layout: {
      title: { text: "Bands used in the fit", x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: kpathAxis(p.ticks, p.tick_labels, p.x_range), yaxis: { range: p.window, title: { text: "E − E<sub>F</sub> (eV)" }, zeroline: false, gridcolor: GRID },
      shapes: [...nodeLines(p.ticks, "x", "y"), efLine("x", "y")],
      legend: { orientation: "h", x: 1, xanchor: "right", y: 1.1 },
      margin: { l: 60, r: 20, t: 55, b: 40 },
    },
  };
}

/** Tight-binding vs DFT on the fitted window, RMSE in the corner (the desktop's draw_fit). */
export function fitFigure(r: FitResult) {
  const data: any[] = [];
  r.e_dft.forEach((y, i) => data.push({ x: r.x, y, type: "scattergl", mode: "lines", line: { color: INK, width: 1.5 },
    name: "DFT", legendgroup: "dft", showlegend: i === 0, hovertemplate: "DFT %{y:.4f} eV<extra></extra>" }));
  r.e_tb.forEach((y, i) => data.push({ x: r.x, y, type: "scattergl", mode: "lines", line: { color: ORANGE, width: 1.7, dash: "dash" },
    name: "Tight-binding fit", legendgroup: "tb", showlegend: i === 0, hovertemplate: "TB %{y:.4f} eV<extra></extra>" }));
  let lo = Infinity, hi = -Infinity;
  for (const b of [...r.e_dft, ...r.e_tb]) for (const v of b) { if (v == null) continue; if (v < lo) lo = v; if (v > hi) hi = v; }
  if (!(lo <= hi)) { lo = -1; hi = 1; }
  const ticks = r.ticks, x0 = r.x[0] ?? 0, x1 = r.x[r.x.length - 1] ?? 1;
  return {
    data,
    layout: {
      title: { text: `Tight-binding vs DFT (${r.non_orthogonal ? "non-orthogonal" : "orthogonal"})`, x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: kpathAxis(ticks, r.tick_labels, [x0, x1]),
      yaxis: { range: [lo - 0.3, hi + 0.3], title: { text: "E − E<sub>F</sub> (eV)" }, zeroline: false, gridcolor: GRID },
      shapes: [...nodeLines(ticks, "x", "y"), efLine("x", "y")],
      annotations: [{ xref: "paper", yref: "paper", x: 0.01, y: 0.99, xanchor: "left", yanchor: "top", showarrow: false,
        text: `RMSE = ${r.rmse_meV.toFixed(1)} meV`, bgcolor: "#fcfcfb", bordercolor: GRID, borderpad: 5, font: { color: "#52514e" } }],
      legend: { orientation: "h", x: 1, xanchor: "right", y: 1.1 },
      margin: { l: 60, r: 20, t: 55, b: 40 },
    },
  };
}

export function convergenceFigure(n: number[], rmse: number[]) {
  return {
    data: [{ x: n, y: rmse, type: "scatter", mode: "lines", line: { color: BLUE, width: 2 }, hovertemplate: "%{x} evaluations<br>%{y:.2f} meV<extra></extra>" }],
    layout: {
      title: { text: "Fit progress", x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: { title: { text: "function evaluations" }, showgrid: false },
      yaxis: { title: { text: "best RMSE (meV)" }, type: "log", gridcolor: GRID },
      margin: { l: 60, r: 20, t: 45, b: 45 },
    },
  };
}
